import { useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import { IconButton } from "../primitives/IconButton";
import { InlineStatusHint, type InlineStatusHintTone } from "../primitives/InlineStatusHint";
import { useLiveAnnouncement } from "../primitives/useLiveAnnouncement";
import type { UiTheme } from "../theme";

// The editor's save line (#336, brief §9), under the top bar as in the v3
// prototype: four plain states (saved, not saved, saving, offline), no time
// (#338). The dialogs and the icon say a draft is kept on the device.
// One rule decides it (editorSaveTone).
// ⛔ «Queued offline» needs a write seam that can answer `queued`: mobile
// only. web-rnw's durableWrite never does.
// a11y: the line is announced only when its tone changes.
// ↶ ↷ sit at the right of the line, not in the bar: at 320 pt in German they
// left the bar's title one letter. The text wraps on the left, beside them.

export type EditorSaveState = {
  busy: boolean;
  /** The edits differ from the server's copy. */
  dirty: boolean;
  /** This editor's last successful write, if any. */
  lastSave: { outcome: "sent" | "queued"; at: number } | null;
  /** Nothing of this program is on the server yet. */
  isCreate: boolean;
};

/** ↶ ↷ (useProgramEditor's undoRedo). A disabled arrow says there is nothing
 *  to take back, to a screen reader too. Absent (a Plan): no arrows. */
export type EditorHistory = {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

type Line = { tone: InlineStatusHintTone; key: "saving" | "unsaved" | "queued" | "saved" };

export function editorSaveTone(s: EditorSaveState): Line {
  if (s.busy) return { tone: "saving", key: "saving" };
  if (s.dirty) return { tone: "draft", key: "unsaved" };
  if (s.lastSave?.outcome === "queued") return { tone: "queued", key: "queued" };
  if (s.lastSave) return { tone: "saved", key: "saved" };
  if (s.isCreate) return { tone: "draft", key: "unsaved" };
  return { tone: "saved", key: "saved" };
}

// English fallbacks, the repo's `defaultValue` convention; the key is
// dynamic, so they sit in one table instead of at each call.
const DEFAULTS: Record<Line["key"], string> = {
  saving: "Saving…",
  unsaved: "Not saved",
  queued: "Offline",
  saved: "Saved",
};

export function EditorSaveStatus({
  theme,
  state,
  history,
}: {
  theme: UiTheme;
  state: EditorSaveState;
  history?: EditorHistory;
}) {
  const { t } = useTranslation();
  const line = editorSaveTone(state);
  const text = t(`program.saveStatus.${line.key}`, { defaultValue: DEFAULTS[line.key] });
  const { speak, region } = useLiveAnnouncement();
  // Only a change of tone is news. Not the first render: opening the editor
  // is not an event.
  const tone = line.tone;
  const prevTone = useRef<InlineStatusHintTone | null>(null);
  const spokenRef = useRef(text);
  spokenRef.current = text;
  useEffect(() => {
    if (prevTone.current !== null && prevTone.current !== tone) speak(spokenRef.current);
    prevTone.current = tone;
  }, [tone, speak]);
  const styles = makeStyles(theme);
  return (
    <View style={styles.row}>
      <View style={styles.text}>
        <InlineStatusHint
          theme={theme}
          tone={tone}
          text={text}
          accessibilityText={text}
          live={false}
        />
      </View>
      {history ? (
        <View style={styles.history}>
          <IconButton
            theme={theme}
            name="undo"
            variant="bare"
            accessibilityLabel={t("program.undo", { defaultValue: "Undo" })}
            disabled={!history.canUndo}
            hitSlop={PAIR_SLOP}
            onPress={history.onUndo}
          />
          <IconButton
            theme={theme}
            name="redo"
            variant="bare"
            accessibilityLabel={t("program.redo", { defaultValue: "Redo" })}
            disabled={!history.canRedo}
            hitSlop={PAIR_SLOP}
            onPress={history.onRedo}
          />
        </View>
      ) : null}
      {region}
    </View>
  );
}

// 36 + 4 + 4 = 44 each way. Across, the s3 gap is the two slops, so the
// zones meet without overlapping (IconButton's default 8 would); up, the
// slop stays inside the line's own s2 padding, clear of Save's.
const PAIR_SLOP = { top: 4, bottom: 4, left: 4, right: 4 };

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    row: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    text: { flex: 1, minWidth: 0 },
    history: { flexDirection: "row", alignItems: "center", gap: s.s3 },
  });
}
